import { cn } from '../../lib/utils';
import {
  formatPercent,
  ragDotClass,
  ragLabel,
  serviceRequestPriorityLabel,
  serviceRequestPriorityTone,
  stageLabel,
  stageTone,
} from '../../lib/service-request-views';

export function StageChip({ stage, name }: { stage: string | null | undefined; name?: string | null }) {
  const label = name && name.trim().length > 0 ? name : stageLabel(stage);
  return (
    <span className={cn('inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium', stageTone(stage))}>
      {label}
    </span>
  );
}

export function ServiceRequestPriorityChip({ priority }: { priority: string | null | undefined }) {
  const label = serviceRequestPriorityLabel(priority);
  if (label === '—') return <span className="text-muted-foreground/50">—</span>;
  return (
    <span className={cn('inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium', serviceRequestPriorityTone(priority))}>
      {label}
    </span>
  );
}

export function RagDot({ rag, withLabel = false }: { rag: string | null | undefined; withLabel?: boolean }) {
  const label = ragLabel(rag);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={label}>
      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', ragDotClass(rag))} aria-hidden />
      {withLabel ? <span>{label}</span> : <span className="sr-only">{label}</span>}
    </span>
  );
}

/** Reading key for the health colours, so a dot never has to be guessed at. */
export function HealthLegend({ className }: { className?: string }) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-4 text-xs text-muted-foreground', className)}>
      {(['green', 'at_risk', 'blocked'] as const).map((rag) => (
        <li key={rag} className="inline-flex items-center gap-1.5">
          <span className={cn('h-2.5 w-2.5 rounded-full', ragDotClass(rag))} aria-hidden />
          {ragLabel(rag)}
        </li>
      ))}
    </ul>
  );
}

export function ReadinessBar({ percent, className, size = 'sm' }: { percent: number | null | undefined; className?: string; size?: 'sm' | 'lg' }) {
  const value = percent == null || !Number.isFinite(percent) ? 0 : Math.min(100, Math.max(0, Math.round(percent)));
  const tone = value >= 100 ? 'bg-emerald-500' : value >= 60 ? 'bg-primary' : value >= 30 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className={cn('flex items-center gap-2', className)} title={`${value}% ready`}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-label="Readiness"
        className={cn('flex-1 overflow-hidden rounded-full bg-muted', size === 'lg' ? 'h-2.5' : 'h-1.5 min-w-[3rem]')}
      >
        <div className={cn('h-full rounded-full transition-[width]', tone)} style={{ width: `${value}%` }} />
      </div>
      <span className={cn('shrink-0 tabular-nums text-muted-foreground', size === 'lg' ? 'text-sm' : 'text-xs')}>
        {formatPercent(value)}
      </span>
    </div>
  );
}
